import { getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { getPageSettings } from "../util/pageSettings";

/**
 * MarketingLandingPage class handles promotional content display on marketing pages
 * Implements singleton pattern to ensure only one instance exists
 */
export class MarketingLandingPage {
    /** Singleton instance of MarketingLandingPage */
    private static instance: MarketingLandingPage;

    /** Mock data for testing and development */

    private readonly fakeData: any;
    /** Flag to control whether to use fake data or real API data */
    private readonly useFakeData: boolean = false;

    private seax: {[key: string]: string[]} = {};

    private readonly promoObject: any = {};

    private couponCodeParam: string = "";

    private disclaimerText: string = "";

    private discountTitle: string = "";

    private readonly useAdditionalStyle: boolean = true;
    
    /**
     * Returns the singleton instance of MarketingLandingPage
     * Creates a new instance if one doesn't exist
     * @returns The singleton instance of MarketingLandingPage
     */
    public static getInstance(): MarketingLandingPage {
        if (!MarketingLandingPage.instance) {
            MarketingLandingPage.instance = new MarketingLandingPage();
        }
        return MarketingLandingPage.instance;
    }
    
    /**
     * Private constructor to enforce singleton pattern
     * Initializes the class and sets up fake data for testing
     */
    private constructor() {
        this.init();
        this.fakeData = {
            "pc":[
                    {
                        "promoCode" : "MAMA",
                        "promoMessageId" : 1,
                        "promoMessage" : "Why wait? What is your time worth? Book now & save {discount}!",
                        "promoCodeId" : 1,
                    },
                    {
                        "promoCode" : "APPLIANCE",
                        "promoMessageId" : 17,
                        "promoMessage" : `{discount} for Single Appliance Removal*<i>(Cannot be combined, excludes subzero refrigerators, and hot tubs)</i>`,
                        "promoCodeId" : 17,
                    }
            ],
            "s":[
                    {
                        "discountID" : 1,
                        "discountValue" : 20,
                        "unit" : "$"
                    },
                    {
                        "discountID" : 2,
                        "discountValue" : 10,
                        "unit" : "%"
                    }
            ],
            "seax":[
                    {
                        "promoCode" : "SUMMER",
                        "promoMessageId" : 1,
                        "promoMessage" : "Why wait? What is your time worth? Book now & save!",
                        "promoCodeId" : 1,
                    },
                    {
                        "promoCode" : "WINTER",
                        "promoMessageId" : 1,
                        "promoMessage" : "Why wait? What is your time worth? Book now & save!",
                        "promoCodeId" : 1,
                    },
                    {
                        "promoCode" : "SPRING",
                        "promoMessageId" : 1,
                        "promoMessage" : "Why wait? What is your time worth? Book now & save!",
                        "promoCodeId" : 1,
                    },
                    {
                        "promoCode" : "FALL",
                        "promoMessageId" : 1,
                        "promoMessage" : "Why wait? What is your time worth? Book now & save!",
                        "promoCodeId" : 1,
                    },
            ],
           "defaultText": "Why wait? What is your time worth? Book now & save $20!",
           "defaultCoupon": "N/A"
        };

    }

    /**
     * Initializes the marketing landing page by checking for URL parameters
     * and handling them for both conditions
     */
    init(){
        const params = this.checkForParams();
        if(params.pcParam || params.sParam)
            this.handleParams({pcParam: params.pcParam ?? '', sParam: params.sParam ?? ''});
        else
            this.attachDefaultOffersWhenNoParams();
    }

    /**
     * Sanitizes input to prevent XSS and injection attacks
     * @param input - The input string to sanitize, may be null
     * @returns Sanitized string or empty string if input was null
     */
    private sanitizeInput(input: string | null): string {
        if (!input) return '';
        // Remove potentially dangerous characters and patterns
        return input
            .replace(/[<>]/g, '') // Remove angle brackets to prevent HTML injection
            .replace(/javascript:/gi, '') // Prevent JavaScript protocol
            .trim();
    }

    /**
     * Sets seasonal parameters based on current month
     * Used by the 'seax' promo code system to automatically determine seasonal promotions
     * 
     * Season mapping:
     * - WINTER: December, January, February
     * - SPRING: March, April, May  
     * - SUMMER: June, July, August
     * - FALL: September, October, November
     * 
     * @returns Current season code (WINTER, SPRING, SUMMER, or FALL)
     */
    setSeasonalParams(): string {
        // Define the seasonal mapping - each season contains its corresponding months
        // This mapping is used to automatically determine which seasonal promo to display
        this.seax = {
            "WINTER": ['December', 'January', 'February'],
            "SPRING": ['March', 'April', 'May'],
            "SUMMER": ['June', 'July', 'August'],
            "FALL": ['September', 'October', 'November']
        }
        
        // Create array of month names indexed by JavaScript Date.getMonth() (0-11)
        // getMonth() returns 0 for January, 1 for February, etc.
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        
        // Get the current month name using the current date
        const currentMonthName = monthNames[new Date().getMonth()];
        
        // Search through each season to find which one contains the current month
        // This determines the appropriate seasonal promo code to use
        const currentSeason = Object.keys(this.seax).find(season => 
            this.seax[season].includes(currentMonthName));
            
        // Return the matching season code, or empty string if no match found
        return currentSeason ?? '';
    }

    /**
     * Extracts and sanitizes URL parameters related to promotions
     * @returns Object containing sanitized URL parameters
     */
    
    checkForParams() {
        // Get the complete current URL including query parameters
        const fullUrl = window.location.href;
        
        // Create URL object for safe and reliable URL parsing
        const url = new URL(fullUrl);
        
        // Extract query parameters using URLSearchParams for proper URL decoding
        const queryParams = new URLSearchParams(url.search);
        
        // Extract and sanitize promotional parameters to prevent XSS and injection attacks
        // 'pc' = promotion code parameter (e.g., ?pc=SUMMER or ?pc=seax)
        const pcParam = this.sanitizeInput(queryParams.get('pc'));
        
        // 's' = discount/special offer parameter (e.g., ?s=1 or ?s=2)
        const sParam = this.sanitizeInput(queryParams.get('s'));
        
        // 'lname' = additional parameter for future use (currently extracted but not processed)
        const lName = this.sanitizeInput(queryParams.get('lname'));

        // Return sanitized parameters as an object for further processing
        return { pcParam, sParam, lName };
    }

    /**
     * Processes URL parameters to retrieve and display promotion content
     * @param params - Object containing promotion code and discount parameters
     */
    handleParams(params: { pcParam: string, sParam: string}) {
        // Extract promotional parameters from the params object
        const pcParam = params.pcParam;  // Promotion code parameter (e.g., 'SUMMER', 'seax')
        const sParam = params.sParam;    // Discount/special offer parameter (e.g., '1', '2')
        
        // Process promotional parameters through the complete promotional pipeline
        // This includes data fetching, promo code matching, discount lookup, and HTML generation
        this.getDisplayText(pcParam, sParam).then((text) => {
            // Display the generated promotional content on the page
            // This safely injects the HTML into designated promotional banner elements
            this.displayContent(text);
        }).catch((error) => {
            // Handle any errors in the promotional processing pipeline
            // Errors could include network issues, data parsing problems, or DOM manipulation failures
            console.error('Error getting display text:', error);
        });
    }

    /**
     * Asynchronously retrieves and formats promotional display text based on parameters
     * @param pc - Promotion code parameter
     * @param s - Discount/special offer parameter
     * @returns Promise resolving to HTML string for display
     */
    getDisplayText(pc: string, s: string): Promise<string> {
        // Fetch promotional data and process it through the promotion pipeline
        return this.getData().then((data) => {
            // Step 1: Find the matching promotion code in the data
            // Handles both regular promo codes and special 'seax' seasonal codes
            this.couponCodeParam = data?.defaultCoupon ?? "";
            const promoCode = this.findPromoCode(data, pc);
            
            // Step 2: Find the matching discount information based on discount ID
            const discount = this.findDiscount(data, s);
            
            // Step 3: Build the complete message content by combining promo and discount data
            // This handles message formatting, discount substitution, and disclaimer extraction
            const messageData = this.buildMessageContent(data, promoCode, discount);
            
            this.disclaimerText = messageData.disclaimer ?? "";
            // Step 4: Construct the final HTML DOM structure for display
            // Uses the formatted message, promo code, and disclaimer
            return this.constructDummyDom(
                messageData.finalMessage,
                promoCode ? promoCode.promoCode : '', // Use promo code if found, empty string otherwise
                messageData.disclaimer
            );
        }).catch(error => {
            // Handle any errors in the promotion processing pipeline
            console.error('Error processing promotion data:', error);
            // Return empty DOM structure as fallback to prevent display issues
            return this.constructDummyDom('', '', '');
        });
    }
    
    /**
     * Finds matching promotion code in the data
     * @param data - The promotion data object
     * @param pc - The promotion code to search for
     * @returns Matching promotion code object or null if not found
     */
    private findPromoCode(data: any, pc: string): any | null {
        if (!pc || !data) return null;
        
        // Special handling for 'seax' parameter - seasonal promo codes
        // When 'seax' is passed as the promo code, the system automatically
        // determines the current season and looks for a matching seasonal promo
        // This allows for dynamic seasonal promotions without hardcoding dates
        if(pc.toLowerCase() === 'seax'){
            // Get the current season code based on the current month
            // setSeasonalParams() returns: WINTER, SPRING, SUMMER, or FALL
            const seasonCode = this.setSeasonalParams();
            
            // Search in the 'seax' data array for a promo code matching the current season
            // This enables automatic seasonal promotion switching throughout the year
            return data.seax?.find((item: any) => 
                (item.promoCode || '').toLowerCase() === seasonCode.toLowerCase()
            );
        }
        else
        return data.pc?.find((item: any) => 
            (item.promoCode || '').toLowerCase() === pc.toLowerCase()
        );
        
    }
    
    /**
     * Finds matching discount in the data
     * @param data - The promotion data object
     * @param s - The discount ID to search for
     * @returns Matching discount object or null if not found
     */
    private findDiscount(data: any, s: string): any | null {
        // Return null if no discount parameter is provided
        if (!s) return null;
        
        // Search through the discount data array ('s') to find matching discount
        // Convert discountID to string and perform case-insensitive comparison
        // This handles both numeric and string discount IDs from URL parameters
        return data.s.find((item: any) => 
            (item.discountID || '').toString().toLowerCase() === s.toLowerCase()
        );
    }
    
    /**
     * Builds the promotional message content based on promo code and discount
     * @param data - The promotion data object
     * @param promoCode - The matched promotion code object
     * @param discount - The matched discount object
     * @returns Object containing formatted message and disclaimer
     */
    private buildMessageContent(data: any, promoCode: any, discount: any): { finalMessage: string, disclaimer: string } {
        // Initialize with default fallback message if no specific promo is found
        let finalMessage = data?.defaultText ?? '';
        let disclaimer = '';
        
        // Store the promo code in the global promo object for later use
        this.promoObject.couponCode = promoCode?.promoCode;
        
        // Determine message content based on available promo code and discount data
        if (promoCode && discount) {
            // Best case: Both promo code and discount available
            // Format message with discount value substitution (e.g., "save {discount}" -> "save $20")
            finalMessage = this.formatMessageWithDiscount(promoCode.promoMessage, discount);
        } else if (promoCode && !discount) {
            // Promo code exists but no discount data
            // Use promo message without discount substitution or fallback to default
            finalMessage = this.getMessageWithoutDiscount(promoCode, data);
        }
        // If neither promoCode nor discount exist, finalMessage remains as defaultText
        
        // Extract any disclaimer text enclosed in <i> tags from the message
        disclaimer = this.extractDisclaimer(finalMessage);
        if (disclaimer) {
            // Remove the disclaimer from the main message to display separately
            finalMessage = finalMessage.replace(`<i>${disclaimer}</i>`, '');
        }
        
        // Store the final processed message in the global promo object
        this.promoObject.promoMessage = finalMessage;
        
        // Return both the clean message and extracted disclaimer
        return { finalMessage, disclaimer };
    }
    
    /**
     * Formats a promotional message by replacing discount placeholders
     * @param message - Message template containing {discount} placeholder
     * @param discount - Discount object with value and unit
     * @returns Formatted message with actual discount value
     */
    private formatMessageWithDiscount(message: string, discount: any): string {
        // Build the formatted discount string based on the discount unit type
        let discountValue = '';
        
        // Handle dollar amounts: place currency symbol before the value (e.g., "$20")
        if (discount.unit === "$") {
            discountValue = `${discount.unit}${discount.discountValue ?? "0"}`;
        } else {
            // Handle percentage and other units: place unit after the value (e.g., "10%", "5 points")
            discountValue = `${discount.discountValue ?? "0"}${discount.unit ?? ""}`;
        }
        if(discountValue){
            this.discountTitle = `${discountValue} Off!`;
        }
        
        // Replace the {discount} placeholder in the message template with the formatted discount value
        // Example: "Save {discount} on your order!" becomes "Save $20 on your order!"
        return message.replace('{discount}', discountValue);
    }
    
    /**
     * Gets appropriate message when no discount is available
     * @param promoCode - The promotion code object
     * @param data - The promotion data object
     * @returns Default message or promo message without discount placeholder
     */
    private getMessageWithoutDiscount(promoCode: any, data: any): string {
        // Check if the promo message contains a discount placeholder
        if (promoCode.promoMessage.includes('{discount}')) {
            // If message requires discount substitution but no discount is available,
            // fall back to the default text to avoid showing incomplete message like "Save {discount}"
            return data.defaultText ?? '';
        }
        
        // If promo message doesn't require discount substitution, use it as-is
        // This handles messages like "Free shipping on orders over $50" that don't need discount values
        return promoCode.promoMessage;
    }
    
    /**
     * Extracts disclaimer text from a message
     * @param message - Message that may contain disclaimer text within <i> tags
     * @returns Extracted disclaimer text or empty string if none found
     */
    private extractDisclaimer(message: string): string {
        return message?.split('<i>')[1]?.split('</i>')[0] ?? '';
    }

    /**
     * Constructs HTML markup for displaying promotional content
     * @param message - The main promotional message
     * @param promoCode - The promotion code to display
     * @param disclaimer - Any disclaimer text to include
     * @returns HTML string ready for display
     */
    constructDummyDom(message:string, promoCode:string, disclaimer:string):string{
        // Define style constants for consistent theming and easy maintenance
        // Uses CSS custom properties to integrate with the application's design system
        const STYLES = {
            PRIMARY_COLOR: 'var(--primary-500)', // Main brand color from CSS variables
            DISCLAIMER_FONT_SIZE: '1rem'      // Slightly smaller font for disclaimer text
        } as const;
        
        // Build reusable style strings from constants
        const primaryStyle = `color:${STYLES.PRIMARY_COLOR};`;
        const disclaimerStyle = `color:${STYLES.PRIMARY_COLOR}; font-style: italic; font-weight: normal; font-size: ${STYLES.DISCLAIMER_FONT_SIZE};line-height: 1.5rem; display: inline-block;`;
        
        // Initialize empty template string for progressive HTML construction
        let domTemplate = ``;
        
        // Build promotional message section with primary brand styling
        // This is the main promotional text (e.g., "Save $20 on your order!")
        if(message){
            domTemplate = `<span style="${primaryStyle}">${message}</span>`;
        }
        
        // Add promo code section with consistent primary styling
        // Displays the actual code users need to enter (e.g., "use promo code SUMMER")
        if(promoCode){
            domTemplate += `<br/>use promo code <span style="${primaryStyle}">${promoCode}</span>`;
        }
        
        // Add disclaimer section with specialized italic styling
        // Contains legal text or restrictions (e.g., "Cannot be combined with other offers")
        if(disclaimer){
            domTemplate += `<br/><span class="promo__disclaimer" style="${disclaimerStyle}">${disclaimer}</span>`;
        }
        
        // Return the complete HTML template ready for DOM insertion
        return domTemplate;
    }
    
    /**
     * Asynchronously retrieves promotional data
     * Returns fake data for testing or empty object for production
     * @returns Promise resolving to promotion data object
     */
    /**
     * Asynchronously retrieves promotional data
     * Returns fake data for testing or API data for production
     * @returns Promise resolving to promotion data object
     */
    async getData(): Promise<any> {  
        try{
            // Check configuration flag to determine data source
            if(this.useFakeData) {
                // Development/testing mode: return mock data with simulated network delay
                // The 1-second timeout simulates real API response time for testing
                return await new Promise<any>((resolve) => {
                    setTimeout(() => {
                        resolve(this.fakeData);            
                    }, 1000);
                });
            } else {
                // Production mode: fetch real promotional data from API endpoint
                // Construct URL to the offers configuration JSON file
                const url = `${window.location.origin}/config-files/_offers-config.json`;
                
                // Use cached API response with 60-second cache duration
                // Parameters: (url, cacheDurationSeconds, apiKeyNeeded, enableCaching)
                return await getCachedAPIResponse(url, 60, false, true);
            }
        }catch(error){
            // Handle any errors during data fetching (network issues, parsing errors, etc.)
            console.error('Error fetching data:', error);
            // Return empty object as fallback to prevent application crashes
            return {};
        }
    }

    constructOfferMessage(){
        // Build offer object with promotional data for cross-page persistence
        // This object structure matches the expected format for downstream components
        const promoMessage = this.stripHtmlTagsFromMessage(this.promoObject.promoMessage.replace(/\*/g, '') ?? "");
        const offerObj = {
            // Use processed promo code from promoObject, fallback to URL parameter
            'couponCode': this.promoObject.couponCode ?? this.couponCodeParam,
            
            // Placeholder fields for future enhancement - currently empty
            'couponExpiryDate': '', // Future: expiration date for time-sensitive offers
            'description': promoMessage,
            'longDescription': this.disclaimerText,  // Future: detailed offer description
            'title': this.discountTitle ?? "",
            
            // Clean page template name for tracking offer source/context
            // Remove HTML entities (&quot;) and colons for clean identifier
            'source': getPageSettings('pagetemplate').replace(/&quot;|:/g, '') ?? "",
            
            // Clean promotional message by removing asterisk characters
            // Asterisks are often used as markdown-style emphasis that should be removed for display
            'promoMessage': promoMessage,
        }
        
        // Store offer details in session storage for access across page navigation
        // This enables other components (Agnostic Flow) to access promotional data without re-processing
        sessionStorage.setItem('offerDetails', JSON.stringify(offerObj));
    }
    
    /** 
     * Safely updates DOM content without using innerHTML directly
     * @param strToDisplay HTML string to display
     */
    displayContent(strToDisplay: string) {
        // Find all promotional banner elements on the page
        const targetDom = document.querySelectorAll('.hero-banner-promo');
        
        // Exit early if no promotional banner elements exist
        if(targetDom.length === 0) return;
        
        // Use DOMParser for secure HTML string to DOM conversion (prevents XSS attacks)
        // This is safer than using innerHTML which can execute malicious scripts
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(strToDisplay, 'text/html');
        
        // Process each promotional banner element found on the page
        targetDom.forEach((dom) => {
            // Clear existing content completely before adding new content
            // This ensures clean slate and prevents content accumulation
            while (dom.firstChild) {
                dom.removeChild(dom.firstChild);
            }
            
            // Apply consistent styling to the promotional banner
            // These styles ensure promotional content stands out visually
            if(dom){
                (dom as HTMLElement).style.fontSize = '2.05rem';   // Large, attention-grabbing text
                (dom as HTMLElement).style.fontWeight = 'bold';    // Bold text for emphasis
                (dom as HTMLElement).style.color = '#1f2a37';      // Dark color for readability
            }
            
            // Create document fragment for efficient DOM manipulation
            // Fragments allow multiple DOM changes before a single insertion (better performance)
            const fragment = document.createDocumentFragment();
            
            // Clone all parsed nodes from the document body
            // This preserves the original parsed structure while creating new instances
            const allNodes = parsedDoc.body.cloneNode(true).childNodes;
            
            // Move all nodes from the cloned structure to the fragment
            // This loop efficiently transfers all content nodes
            while (allNodes.length > 0) {
                fragment.appendChild(allNodes[0]);
            }
            
            // Single DOM operation to append all promotional content
            // This minimizes DOM reflows and improves performance
            dom.appendChild(fragment);
            this.restyleBannerForOpacity(dom as HTMLElement);
        });
        
        // Store promotional data in session storage for cross-component access
        // This enables the Agnostic Flow and other components to use the promotional data
        this.realTimeCommunicationStyling();
        this.constructOfferMessage();
    }

    /**
     * Applies consistent styling to real-time communication elements and their components
     * 
     * This method handles the complete styling setup for real-time communication widgets:
     * - Applies master typography styles (inherit font-size, normal font-weight)
     * - Hides disclaimer text initially and attaches toggle functionality
     * - Sets image containers to 100% height for consistent layout
     * 
     * @description Processes all elements with '.real-time-communication' class
     * @complexity O(n*m) where n = number of real-time-comm elements, m = average child elements
     * @security Safe DOM manipulation with proper type casting
     */
    realTimeCommunicationStyling(){
        // Find all real-time communication elements on the page
        const realTimeCommunication = document.querySelectorAll('.real-time-communication');
        
        // Only proceed if elements exist to avoid unnecessary processing
        if(realTimeCommunication.length>0){
            // Define consistent typography styles that inherit from parent
            const masterStyle = `font-size: inherit; font-weight: normal;`;
            // Initially hide disclaimer text for clean UI
            const disclaimerStyle = `display:none`;
            
            // Process each real-time communication element
            realTimeCommunication.forEach((element) => {
                // Apply master typography styles to the main element
                (element as HTMLElement).style.cssText = masterStyle;
                
                // Look for disclaimer text within this element
                const disclaimerText = element.querySelector(".promo__disclaimer") as HTMLElement;
                if(disclaimerText){
                    // Hide disclaimer initially and add toggle functionality
                    disclaimerText.style.cssText = disclaimerStyle;
                    this.attachReadMore(disclaimerText);
                }

                // Find the parent list container for image height adjustment
                const closestUi = element.closest('.real-time-comm-list') as HTMLUListElement;
                if(closestUi){
                   // Ensure consistent image container heights across all list items
                   this.setImageHeightAt100(closestUi);
                }
            });
        }
    }

    /**
     * Sets all image containers within a communication list to 100% height
     * 
     * This ensures consistent visual layout by making all image containers
     * fill their parent container completely, preventing layout inconsistencies
     * caused by varying image dimensions.
     * 
     * @param closestUi - The UL element containing the communication list items
     * @description Iterates through all li elements and sets .real-time-comm-image height to 100%
     * @complexity O(n) where n = number of list items
     * @security Safe DOM traversal with null checks
     */
    setImageHeightAt100(closestUi: HTMLUListElement){
        // Find all list item elements within the provided UL container
        // This targets each individual communication item in the list
        const liList = closestUi.querySelectorAll('li');
        
        // Process each list item to standardize image container heights
        liList.forEach((li) => {
            // Look for the image container within this specific list item
            // Uses class selector to target the designated image wrapper element
            const imageContainer = li.querySelector(".real-time-comm-image") as HTMLElement;
            
            // Only apply height styling if the image container exists
            // This prevents errors when some list items may not have images
            if(imageContainer){
                // Set height to 100% to fill the parent container completely
                // This ensures all image containers have uniform height regardless of content
                imageContainer.style.height = "100%";
            }
        });
    }

    /**
     * Attaches a toggle-able "Read More/Read Less" functionality to disclaimer text
     * 
     * Creates an interactive element that allows users to show/hide disclaimer content:
     * - Initially hides the disclaimer text
     * - Creates a clickable "Read More..." button
     * - Toggles between showing/hiding disclaimer with appropriate button text
     * - Adjusts margins for better visual spacing
     * 
     * @param disclaimerText - The HTML element containing disclaimer content
     * @description Implements collapsible disclaimer with toggle functionality
     * @complexity O(1) - Single DOM element creation and event attachment
     * @security Uses textContent instead of innerHTML to prevent XSS
     * @ux Provides smooth toggle experience with visual feedback
     */
    attachReadMore(disclaimerText: HTMLElement){
        // Define styling for the visible disclaimer text when expanded
        // Uses CSS custom properties for brand consistency and proper visual hierarchy
        const disclaimerStyle = `color:var(--primary-500);font-style: italic;font-weight: normal;font-size: 0.8rem;line-height: 1.2rem;margin: 1rem 0;opacity: 0.8;display: inline-block;`

        // Define styling for the clickable "Read More/Less" toggle button
        // Inherits color from parent while maintaining interactive appearance
        const promptStyle = 'display:block; cursor:pointer; color:inherit; margin: 1rem 0;font-style: italic;font-weight: normal;font-size: 0.8rem;'

        // Define constant text labels for the toggle states
        // Using constants prevents typos and makes text changes easier
        const READ_MORE_TEXT = "Read More...";
        const READ_LESS_TEXT = "Read Less...";

        // Create the interactive toggle button element
        // Uses div for semantic neutrality and full styling control
        const readMore = document.createElement('div');
        
        // Set initial button text and apply interactive styling
        // Uses textContent for XSS security instead of innerHTML
        readMore.textContent = READ_MORE_TEXT;
        readMore.style.cssText = promptStyle;
        
        // Insert the toggle button immediately after the disclaimer element
        // 'afterend' places it as a sibling, not a child, for better semantic structure
        disclaimerText.insertAdjacentElement('afterend', readMore);
        
        // Add click event listener for toggle functionality
        readMore.addEventListener("click", () => {
            // Check current visibility state of disclaimer text
            if (disclaimerText.style.display === 'none') {
                // Show disclaimer: apply full styling and update button text
                disclaimerText.style.cssText = disclaimerStyle;
                readMore.textContent = READ_LESS_TEXT;
                // Remove top margin when disclaimer is visible for better visual flow
                readMore.style.marginTop = '0px';
            } else {
                // Hide disclaimer: set display none and reset button text
                disclaimerText.style.display = 'none';
                readMore.textContent = READ_MORE_TEXT;
                // Add top margin when disclaimer is hidden for proper spacing
                readMore.style.marginTop = '1rem';
            }
        });
    }

    /**
     * Removes all HTML tags from a string while preserving text content and normalizing whitespace.
     * Handles block-level elements by replacing them with a single space to maintain word separation.
     * 
     * @param {string} stringToStrip - The HTML string to process. Can contain any HTML markup.
     * @returns {string} A plain text version of the input with all HTML tags removed and whitespace normalized.
     * 
     * @example
     * // Returns "Hello World! Click here"
     * stripHtmlTagsFromMessage('<p>Hello <strong>World</strong>!</p> <a href="#">Click here</a>');
     * 
     * @example
     * // Returns "Line 1 Line 2Line 3"
     * stripHtmlTagsFromMessage('Line 1<br/>Line 2<hr>Line 3');
     * 
     * @example
     * // Returns "" (empty string)
     * stripHtmlTagsFromMessage(null);
     */
    stripHtmlTagsFromMessage(stringToStrip: string): string {
        if (!stringToStrip) return '';
    
        const tagPattern = /<\/?[a-z][a-z\d]*(?:\s+[a-z\d-]+(?:=(?:"[^"]*"|'[^']*'|[^\s>]+))?)*\s*\/?>/gi;
        return stringToStrip
            // Replace specific block-level tags with a space
            .replace(/<\/?(p|div|br|li|tr|td|th|ul|ol|h[1-6]|table)[^>]*>/gi, ' ')
            // Remove all other tags
            .replace(tagPattern, '')
            // Collapse multiple spaces and trim
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Attaches default offers to the marketing landing page when no URL parameters are present.
     * This method serves as a fallback to display default content when the page is loaded
     * without any specific promotional or service parameters.
     * 
     * @example
     * // When no URL parameters are present
     * marketingPage.attachDefaultOffersWhenNoParams();
     * 
     * @returns {void}
     * 
     * @memberof MarketingLandingPage
     * @instance
     * @private
    */
    attachDefaultOffersWhenNoParams() {
        this.handleParams({pcParam: '', sParam: ''});
    }

    /**
     * Applies styling to a hero banner promo element to ensure better text visibility
     * when displayed over a translucent or complex background.
     *
     * - Adjusts background gradient for contrast.
     * - Inherits text color for the promo and disclaimer elements.
     * - Optionally applies additional styles like image blur if `useAdditionalStyle` is enabled.
     *
     * @param {HTMLElement} heroBannerPromo - The DOM element containing the promo content within the hero banner.
    */
    restyleBannerForOpacity(heroBannerPromo : HTMLElement){
        const bannerWrap = heroBannerPromo?.closest('.hero-banner-wrap') as HTMLElement;
        if(bannerWrap?.classList.contains('translucent-layer'))
            return;
        const heroBannerOverlay = heroBannerPromo?.closest('.hero-banner-overlay') as HTMLElement;
        if(!heroBannerPromo || !heroBannerOverlay){
            return;
        }
        heroBannerOverlay.style.background = 'linear-gradient(90deg, rgba(0, 0, 0, .9), hsla(0, 6%, 44%, 0) 96.68%)';
        heroBannerPromo.style.color = 'inherit';
        const disclaimerText = heroBannerPromo.querySelector('.promo__disclaimer') as HTMLElement;
        if(disclaimerText){
            disclaimerText.style.color = 'inherit';
            disclaimerText.style.opacity = '0.8';
        }
        if(this.useAdditionalStyle){
            this.imageBlurrer(heroBannerPromo);
        }
    }

    /**
     * Applies a blur effect to the background image within a hero banner.
     * This enhances foreground text readability by softening the image details.
     *
     * - Slightly enlarges and repositions the image to compensate for blur clipping.
     * - Applies a blur filter via CSS.
     *
     * @param {HTMLElement} heroBannerPromo - The DOM element inside the hero banner used to locate the image container.
    */
    imageBlurrer(heroBannerPromo:HTMLElement){
        const bannerDom = heroBannerPromo.closest('.hero-container') as HTMLElement;
        if(!bannerDom){
            return;
        }
        const bannerImage = bannerDom.querySelector('.hero-banner-img') as HTMLImageElement;
        if(!bannerImage){
            return;
        }
        bannerDom.style.overflow = 'hidden';
        
        const bannerImageStyle = `width: 104%; height: 104%; left: 4px; top: -4px; filter: blur(2px);`
        bannerImage.style.cssText = bannerImageStyle;
    }
    
}



/**
 * Event listener that initializes the marketing landing page when DOM is loaded
 * Only initializes if the page template includes 'campaign page template'
 */
document.addEventListener('DOMContentLoaded', () => {
    // Get the current page template setting to determine if marketing features should be enabled
    const pageTemplate = getPageSettings('pagetemplate');
    
    // Only initialize marketing functionality on campaign pages
    // This prevents unnecessary processing on non-promotional pages
    if(!pageTemplate?.toLowerCase().includes('campaign page template'))
        return

    // Get the singleton instance of MarketingLandingPage
    // Singleton pattern ensures only one instance exists across the application
    const marketingLandingPage = MarketingLandingPage.getInstance();
    
    // Initialize the marketing landing page functionality
    // This starts URL parameter checking and promotional content processing
    marketingLandingPage.init();
})